/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useCallback } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

// Material Dashboard 2 React components

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import MDButton from "components/MDButton";

// Material Dashboard 2 React examples
import APIDataTable from "customComponents/Tables/APIDataTable";
import { useAlert } from "customComponents/Alert";

import AddDialog from "./AddDialog";

// Data

import PropTypes from "prop-types";
import { addDepartment, getDepartments, deleteDepartment, editDepartment } from "helper";
import DashboardNavbar from "customComponents/Navbars/DashboardNavbar";
import DashboardLayout from "customComponents/LayoutContainers/DashboardLayout";
import Footer from "customComponents/Footer";

import { formatTime, isObjectEqual } from "../../utils";

function DepartmentData({ department }) {
  const [menu, setMenu] = useState(null);
  const [addDialog, setAddDialog] = useState(false);
  const alert = useAlert();

  const [departmentsData, setDepartmentsData] = useState([]);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [filter, setFilter] = useState({});

  const columns = [
    { Header: "name", accessor: "name", width: "25%", align: "left" },
    { Header: "description", accessor: "description", align: "center" },
    { Header: "action", accessor: "action", width: "25%", align: "center" },
  ];

  useEffect(() => {
    fetchData();
  }, [page, limit, filter]);

  const fetchData = useCallback(async () => {
    try {
      const { search, ...reset } = filter;
      let payload = { page, limit, ...reset, sortBy: "createdAt:desc" };

      if (search && search !== "") {
        payload["name"] = search;
      }

      const res = await getDepartments(payload);

      const { results, totalPages, totalResults } = res.data;

      const formattedData = formatData(results);

      setDepartmentsData(formattedData);
      setTotalPages(totalPages);
      setTotalResults(totalResults);

      console.log(res);
    } catch (error) {
      console.log("error", error);

      const title = error.response ? error.response.statusText : "Unknown error";
      const message =
        error.response && error.response.data ? error.response.data.message : error.message;

      alert.show(title, message);
    }
  }, [page, limit, filter]);

  const addDepartmentRequest = useCallback(
    async (payload, onSuccess) => {
      try {
        await addDepartment(payload);

        fetchData();

        onSuccess && onSuccess();
      } catch (error) {
        console.log("Add Department Error", error);

        const title = error.response ? error.response.statusText : "Unknown error";
        const message =
          error.response && error.response.data ? error.response.data.message : error.message;

        alert.show(title, message);
      }
    },
    [page, limit, filter]
  );

  const editDepartmentRequest = useCallback(
    async (id, payload, onSuccess) => {
      try {
        await editDepartment(id, payload);

        fetchData();

        onSuccess && onSuccess();
        alert.show("success", "Updated Successfully");
      } catch (error) {
        console.log("Edit Department Error", error);

        const title = error.response ? error.response.statusText : "Unknown error";
        const message =
          error.response && error.response.data ? error.response.data.message : error.message;

        alert.show(title, message);
      }
    },
    [page, limit, filter]
  );

  const deleteDepartmentRequest = useCallback(
    async (id, onSuccess) => {
      try {
        await deleteDepartment(id);

        fetchData();

        onSuccess && onSuccess();
      } catch (error) {
        console.log("Delete Worklog Error", error);

        const title = error.response ? error.response.statusText : "Unknown error";
        const message =
          error.response && error.response.data ? error.response.data.message : error.message;

        alert.show(title, message);
      }
    },
    [page, limit, filter]
  );

  const formatData = (res) => {
    return res.map((value, index) => {
      const { name, description } = value;

      return {
        name: (
          <MDTypography variant="caption" fontWeight="medium">
            {name}
          </MDTypography>
        ),
        description: (
          <MDTypography variant="caption" fontWeight="medium">
            {description}
          </MDTypography>
        ),
        action: (
          <MDTypography component="a" id={value.id} data={JSON.stringify(value)} onClick={openMenu}>
            <Icon>more_vert</Icon>
          </MDTypography>
        ),
      };
    });
  };

  const openMenu = ({ currentTarget }) => {
    setMenu(currentTarget);
  };

  const closeMenu = () => {
    setMenu(null);
  };

  const openAddDialog = (selectedType = "add", id = null, defaultValues = {}) => {
    setAddDialog({ selectedType, id, defaultValues });
  };

  const closeAddDialog = () => {
    setAddDialog(null);
  };

  const handleAdd = () => {
    closeMenu();
    openAddDialog();
  };

  const handleEdit = async () => {
    closeMenu();

    const dataJson = menu.getAttribute("data");
    console.log(dataJson);
    const data = JSON.parse(dataJson);

    openAddDialog("edit", menu.id, data);
  };

  const handleDelete = () => {
    const id = menu.id;
    console.log("department", id);

    closeMenu();

    alert.show(
      "Delete Department",
      `Are you sure you want to delete this department?`,
      ["No", "Yes"],
      (index) => {
        if (index === 1) {
          deleteDepartmentRequest(id);
        }
      }
    );
  };

  const handleSubmit = (data) => {
    const { id, selectedType, ...rest } = data;

    if (selectedType === "add") {
      addDepartmentRequest(rest, closeAddDialog);
    } else {
      const { name, description } = rest;

      if (name || description) {
        editDepartmentRequest(id, rest, closeAddDialog);
      } else {
        alert.show("Edit Department", "You must have at least one key to updete");
      }
    }
  };

  const renderMenu = (
    <Menu
      id="simple-menu"
      anchorEl={menu}
      anchorOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={Boolean(menu)}
      onClose={closeMenu}
    >
      <MenuItem onClick={handleEdit}>Edit</MenuItem>
      <MenuItem onClick={handleDelete}>Delete</MenuItem>
    </Menu>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox pb={3}>
        <Card>
          <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3} pb={0}>
            <MDBox>
              <MDTypography variant="h6">Departments</MDTypography>
            </MDBox>
            <MDBox>
              <MDButton
                // sx={{ cursor: "pointer", fontWeight: "bold" }}
                variant="h6"
                gutterBottom
                // color="text"
                fontSize="small"
                onClick={handleAdd}
              >
                ADD DEPARTMENT
              </MDButton>
            </MDBox>
          </MDBox>
          <MDBox>
            <APIDataTable
              table={{ rows: departmentsData, columns }}
              showTotalEntries={true}
              isSorted={false}
              noEndBorder
              entriesPerPage={false}
              canSearch
              currentPage={page}
              totalPages={totalPages}
              totalResults={totalResults}
              fetchData={(newPage, newLimit, newFilter) => {
                if (newPage !== page) {
                  setPage(newPage);
                }

                if (newLimit !== limit) {
                  setLimit(newLimit);
                }

                if (!isObjectEqual(newFilter, filter)) {
                  if (page !== 1 || newPage !== 1) {
                    setPage(1);
                  }

                  setFilter(newFilter);
                }
              }}
            />
          </MDBox>
          {renderMenu}
          {addDialog && (
            <AddDialog
              open={Boolean(addDialog)}
              {...addDialog}
              onClose={closeAddDialog}
              onSubmit={handleSubmit}
            />
          )}
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

DepartmentData.defaultProps = {
  department: null,
};

// Typechecking props for the DataTable
DepartmentData.propTypes = {
  device: PropTypes.object,
};

export default DepartmentData;
